import React, { useMemo } from 'react';

import { format, parseISO } from 'date-fns';
import { Box, ButtonV2, Divider, Flex, LinkV2, TextV2 } from '@withjoy/joykit';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { Link, useTranslation } from '@shared/core';
import OrderInfo from '@apps/card/components/Blocks/OrderInfo';
import { ProgressTracker } from '../ProgressTracker';
import { StationeryPrintOrderDetailsFragment, StationeryPrintOrderStatusEnum } from '@graphql/generated';
import { PaymentSummary } from './components/PaymentSummary';
import { InlineMessage } from './components/InlineMessage';
import { PRINT_ORDER_STATUS_TO_CONSOLIDATED_STATUS } from '../../OrderDetails.constants';

const OrderMetadata = ({ orderNumber, orderPlaced }: { orderNumber: string; orderPlaced?: string }) => {
  const orderPlacedFormatted = orderPlaced && format(parseISO(orderPlaced), 'PPP');
  return (
    <Flex rowGap={2} flexDirection="column" textAlign={{ md: 'right' }} paddingX={{ _: 6, sm2: 8, md: 0 }}>
      <TextV2 fontWeight="bold" typographyVariant="body1">{`Order Number: ${orderNumber}`}</TextV2>
      {orderPlacedFormatted && <TextV2 typographyVariant="body1">{`Order Placed: ${orderPlacedFormatted}`}</TextV2>}
    </Flex>
  );
};

interface PrintJobDetailsProps {
  order: StationeryPrintOrderDetailsFragment;
  variant: 'overview' | 'detailed';
  getToOrderDetailsPath?: (printOrderNumber: string) => string;
}

export const PrintJobDetails = (props: PrintJobDetailsProps) => {
  const { t2 } = useTranslation('stationery');
  const courierErrorMessage = t2('cardCustomizer', 'courierErrorMessage');

  const { variant, order, getToOrderDetailsPath } = props;
  const isMobileOrTablet = useIsMobileOrTablet();

  const orderNumber = useMemo(() => order.orderNumber, [order.orderNumber]);
  const orderPlaced = useMemo(() => order.printPayment?.paidAt, [order.printPayment?.paidAt]);

  const orderTrackingUrl = useMemo(() => order.trackingNumbers?.[0]?.trackingUrl, [order.trackingNumbers]);

  const shippingAddress = useMemo(() => {
    const { address1, address2, city, name, region, postalCode, countryCode } = order.shippingAddress;
    let addressLine = address1;
    if (address2) {
      addressLine += `, ${address2}`;
    }
    const cityLine = `${city}, ${region} ${postalCode} ${countryCode}`;
    return `${name}\n${addressLine}\n${cityLine}`;
  }, [order.shippingAddress]);

  const lastCanceledAtStatus = useMemo(() => {
    if (!order.statusHistory) {
      return null;
    }

    return [...order.statusHistory].sort((a, b) => new Date(b.date).valueOf() - new Date(a.date).valueOf()).find(entity => entity.status === 'canceled');
  }, [order.statusHistory]);
  const isErrorStatus = useMemo(() => order.currentStatus === StationeryPrintOrderStatusEnum.error, [order.currentStatus]);
  const consolidatedOrderStatus = PRINT_ORDER_STATUS_TO_CONSOLIDATED_STATUS[order.currentStatus] ?? 'orderPlaced';

  const isOrderCanceled = consolidatedOrderStatus === 'canceled';
  const isOrderRefunded = consolidatedOrderStatus === 'refunded';
  const isOrderInNormalFlow = !isOrderCanceled && !isOrderRefunded;

  return (
    <Flex flexDirection="column" rowGap={10}>
      <Flex columnGap={{ md: 10 }} flexDirection={{ _: 'column', md: 'row' }} rowGap={10}>
        <Flex flexDirection="column" rowGap={8} flex={1}>
          {isMobileOrTablet && <OrderMetadata orderNumber={orderNumber} orderPlaced={orderPlaced} />}

          <Flex paddingX={{ _: 6, md: 0 }} justifyContent="center">
            <Box
              as="img"
              src={order.printCardFrontPreviewUrl}
              aspectRatio={'5 / 7'}
              __css={{
                width: { _: 360, md: '100%' }
              }}
            />
          </Flex>
        </Flex>

        <Flex flexDirection="column" paddingY={{ md: 8 }} flex={1} rowGap={10}>
          {!isMobileOrTablet && <OrderMetadata orderNumber={orderNumber} orderPlaced={orderPlaced} />}

          <ProgressTracker status={consolidatedOrderStatus} />
          {isErrorStatus && (
            <InlineMessage
              icon="error"
              message={
                <>
                  {courierErrorMessage.text1}{' '}
                  <LinkV2
                    _activeLink={{ color: 'mono14' }}
                    _visited={{ color: 'mono14' }}
                    _active={{ color: 'mono14' }}
                    _pressed={{ color: 'mono14' }}
                    _hover={{ color: 'mono14' }}
                    color="mono5"
                    fontSize="15px"
                    href={orderTrackingUrl}
                    isExternal
                  >
                    {courierErrorMessage.link}
                  </LinkV2>{' '}
                  {courierErrorMessage.text2}
                </>
              }
            />
          )}
          {isOrderCanceled && <InlineMessage icon="info" title="Refund is Being Processed" message="You will be refunded within 10 business days." />}
          <Flex
            columnGap={10}
            flexDirection={{
              _: 'column',
              md: 'row'
            }}
            rowGap={10}
            width={{ _: '100%', sm2: 400, md: 'unset' }}
            paddingX={{ _: 6, sm2: 0 }}
            alignSelf={{ _: 'center', md: 'unset' }}
          >
            <Box flex={1}>
              {isOrderInNormalFlow && <OrderInfo iconVariant="pin" title="Shipping To" description={shippingAddress} />}
              {isOrderCanceled && (
                <OrderInfo
                  iconVariant="info"
                  title="Order Cancelled"
                  description="Your order has been successfully cancelled and your refund is being processed within 10 business days."
                />
              )}
              {isOrderRefunded && (
                <OrderInfo
                  iconVariant="info"
                  title={`Order Cancelled on ${format(new Date(lastCanceledAtStatus?.date || 0), 'MMMM do, yyyy')}`}
                  description="Your order has been cancelled and your refund has been successfully processed."
                />
              )}
            </Box>

            <Flex flexDirection="column" rowGap={5} flexBasis={{ md: 249 }}>
              {isOrderInNormalFlow ? (
                <>
                  <ButtonV2 as="a" disabled={!orderTrackingUrl} href={orderTrackingUrl} target="_blank" intent="neutral" shape="rounded">
                    Track Your Order
                  </ButtonV2>
                  {variant === 'overview' && getToOrderDetailsPath && (
                    <ButtonV2 as={Link} to={getToOrderDetailsPath(orderNumber)} intent="neutral" variant="outline" shape="rounded">
                      Order Summary
                    </ButtonV2>
                  )}
                </>
              ) : (
                <ButtonV2 as="a" href="mailto:printsupport@withjoy.com" intent="neutral" shape="rounded">
                  Contact Support
                </ButtonV2>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      {variant === 'detailed' && (
        <>
          <Divider />
          <PaymentSummary order={props.order} />
        </>
      )}
    </Flex>
  );
};
